import { JobStatus, JobTrait } from "../service"

export interface JobStatusColors {
  lightColor?: string
  mediumColor?: string
  heavyColor?: string
  textColor?: string
}

export function getJobStatusColors(
  status: JobStatus | JobTrait,
): JobStatusColors {
  switch (status) {
    case JobStatus.BOOKED:
      return {
        lightColor: "secondary-background",
        mediumColor: "border",
        heavyColor: "secondary-foreground",
        textColor: "foreground",
      }
    case JobStatus.CHECKED_IN:
      return {
        lightColor: "lime-50",
        mediumColor: "lime-200",
        heavyColor: "lime-700",
        textColor: "foreground",
      }
    case JobStatus.COLLECTED:
      return {
        lightColor: "info-background",
        mediumColor: "info-border",
        heavyColor: "info-foreground",
        textColor: "foreground",
      }
    case JobStatus.INSPECTION_DRAFT_SAVED:
      return {
        lightColor: "purple-50",
        mediumColor: "decorative-border",
        heavyColor: "decorative-foreground",
        textColor: "foreground",
      }
    case JobStatus.SERVICE_PROPOSAL_SENT:
      return {
        lightColor: "warning-background",
        mediumColor: "warning-border",
        heavyColor: "warning-foreground",
        textColor: "foreground",
      }
    case JobStatus.SERVICE_PROPOSAL_ACCEPTED:
      return {
        lightColor: "notification-background",
        mediumColor: "notification-border",
        heavyColor: "notification-foreground",
        textColor: "foreground",
      }
    case JobStatus.COMPLETED:
      return {
        lightColor: "success-background",
        mediumColor: "success-border",
        heavyColor: "success-foreground",
        textColor: "foreground",
      }
    case JobStatus.DELIVERED:
      return {
        lightColor: "primary-background",
        mediumColor: "primary-border",
        heavyColor: "primary",
        textColor: "foreground",
      }
    case JobStatus.CANCELLED:
      return {
        lightColor: "destructive-background",
        mediumColor: "destructive-border",
        heavyColor: "destructive-foreground",
        textColor: "foreground",
      }
    case JobTrait.WAITING_ON_DELIVERY:
      return {
        lightColor: "amber-200",
        mediumColor: "amber-500",
        heavyColor: "amber-900",
        textColor: "foreground",
      }
    default:
      return {
        lightColor: "secondary-background",
        mediumColor: "border",
        heavyColor: "secondary-foreground",
        textColor: "foreground",
      }
  }
}

function getColorValueByVariant(
  colors: JobStatusColors,
  variant: "light" | "medium" | "heavy",
): string | undefined {
  if (variant === "light") {
    return colors.lightColor
  }
  if (variant === "medium") {
    return colors.mediumColor
  }
  return colors.heavyColor
}

/**
 * Returns a background color className that can be used directly in cn()
 * Example: cn("flex", getBgColor(JobStatus.COLLECTED, "medium")) -> "flex bg-info-border"
 */
export function getBgColor(
  status: JobStatus | JobTrait,
  variant: "light" | "medium" | "heavy",
): string | undefined {
  const colors = getJobStatusColors(status)
  const colorValue = getColorValueByVariant(colors, variant)
  return colorValue ? `bg-${colorValue}` : undefined
}

/**
 * Returns a border color className that can be used directly in cn()
 * Example: cn("flex border", getBorderColor(JobStatus.COLLECTED, "medium")) -> "flex border border-info-border"
 */
export function getBorderColor(
  status: JobStatus | JobTrait,
  variant: "light" | "medium" | "heavy",
): string | undefined {
  const colors = getJobStatusColors(status)
  const colorValue = getColorValueByVariant(colors, variant)
  return colorValue ? `border-${colorValue}` : undefined
}

/**
 * Returns a text color className that can be used directly in cn()
 * Example: cn("flex", getTextColor(JobStatus.COLLECTED, "medium")) -> "flex text-info-border"
 */
export function getTextColor(
  status: JobStatus | JobTrait,
  variant: "light" | "medium" | "heavy",
): string | undefined {
  const colors = getJobStatusColors(status)
  const colorValue = getColorValueByVariant(colors, variant)
  return colorValue ? `text-${colorValue}` : undefined
}

/**
 * Returns a contrasting text color className that can be used directly in cn()
 * This is the text color to use when you have a background color for proper contrast.
 * Example: cn("flex", getContrastTextColor(JobStatus.COLLECTED)) -> "flex text-foreground"
 */
export function getContrastTextColor(
  status: JobStatus | JobTrait,
): string | undefined {
  const colors = getJobStatusColors(status)
  return colors.textColor ? `text-${colors.textColor}` : undefined
}
